import type { CompanyAccountModelDto } from '@treasury/api/channel';
import { TmContainer } from '@treasury/presentation';
import { type SelectItemVm, type TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-bottom-sheet';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import '@treasury/presentation/components/tm-footer';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { defaultAchExceptionsActivitySearchRequest } from '../data/default-ach-exceptions-activity-search-request';
import { mapAccountToVm, mapSecCodeToVm, SecCode } from './ach-exceptions.types';

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.SpecificAmount,
};

const defaultPostedRange: DateRange = {
    rangeType: DateRangeType.None,
};

export const tagName = 'ach-exceptions-activity-filter-sheet';
@customElement(tagName)
export class AchExceptionsActivityFilterSheet extends TmContainer {
    @property({ type: Boolean })
    public open = false;

    @property({ type: Object })
    public filterRequest = defaultAchExceptionsActivitySearchRequest;

    @property({ type: Array })
    public accounts: CompanyAccountModelDto[] = [];

    @property({ type: Array })
    public secCodes: SecCode[] = [];

    @state()
    private selectedAccounts: CompanyAccountModelDto[] = [];

    @state()
    private selectedSecCodes: SecCode[] = [];

    @state()
    private amountRange: CurrencyRange = defaultAmountRange;

    @state()
    private postedDate: DateRange = defaultPostedRange;

    @state()
    private description = '';

    @state()
    private achExceptionDecisionStatus = 'all';

    @state()
    private achExceptionDecisionStatusItems: SelectItemVm<string>[] = [
        { value: 'all', label: 'All' },
        { value: 'Pay', label: 'Pay' },
        { value: 'Reject', label: 'Reject' },
        { value: 'NoDecision', label: 'No Decision' },
    ];

    public firstUpdated() {
        this.filter();
    }

    public render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <div slot="header-center">Filter ACH Exception Activity</div>
            <div>
                <tm-section class="p-3">
                    <tm-select
                        class="mb-3 pt-3"
                        required
                        label="Decision"
                        .items=${this.achExceptionDecisionStatusItems}
                        .initial=${this.achExceptionDecisionStatus}
                        @value-changed=${(e: TmValueChanged<string>) => {
                            if (e.detail.value) {
                                this.achExceptionDecisionStatus = e.detail.value;
                            }
                        }}
                    ></tm-select>
                    <tm-multi-select
                        class="mb-3"
                        required
                        .items=${this.accounts}
                        .allowSelectAll=${true}
                        .allSelectedText=${'All Accounts'}
                        .initial=${[...this.selectedAccounts]}
                        .mapItemToVm=${mapAccountToVm}
                        @value-changed=${(e: TmValueChanged<CompanyAccountModelDto[]>) => {
                            this.selectedAccounts = e.detail.value || [];
                        }}
                        label="Accounts"
                    ></tm-multi-select>
                    <tm-multi-select
                        class="mb-3"
                        required
                        .items=${this.secCodes}
                        .allowSelectAll=${true}
                        .allSelectedText=${'All SEC Codes'}
                        .initial=${[...this.selectedSecCodes]}
                        .mapItemToVm=${mapSecCodeToVm}
                        @value-changed=${(e: TmValueChanged<SecCode[]>) => {
                            this.selectedSecCodes = e.detail.value || [];
                        }}
                        label="SEC Codes"
                    ></tm-multi-select>
                    <tm-currency-range
                        class="mb-3"
                        label="Amount"
                        .currencyRangeOptions=${[
                            CurrencyRangeType.SpecificAmount,
                            CurrencyRangeType.AmountRange,
                        ]}
                        .initial=${this.amountRange}
                        @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                            const { value } = e.detail;
                            if (!value) {
                                return;
                            }
                            this.amountRange = value;
                        }}
                    ></tm-currency-range>
                    <tm-date-range
                        class="mb-3"
                        label="Posted Date"
                        .initial=${this.postedDate}
                        @value-changed=${(e: TmValueChanged<DateRange>) => {
                            const { value } = e.detail;
                            if (value) {
                                this.postedDate = value;
                            }
                        }}
                    ></tm-date-range>
                    <tm-text-field
                        class="pb-3"
                        label="Entry Description"
                        .initial=${this.description}
                        @value-changed=${(e: CustomEvent) => {
                            this.description = e.detail.value;
                        }}
                    ></tm-text-field>
                </tm-section>
                <tm-footer class="absolute"
                    ><tm-button
                        size="large"
                        class="w-full"
                        importance="primary"
                        .disabled=${!this.selectedAccounts.length || !this.selectedSecCodes.length}
                        @click=${() => {
                            this.filter();
                            this.dispatchEvent(new CustomEvent('close'));
                        }}
                        >Apply Filters</tm-button
                    ></tm-footer
                >
            </div>
        </tm-bottom-sheet>`;
    }

    private filter() {
        if (!this.selectedAccounts.length) {
            this.selectedAccounts = [...this.accounts];
        }
        if (!this.selectedSecCodes.length) {
            this.selectedSecCodes = [...this.secCodes];
        }
        this.dispatchEvent(
            new CustomEvent('filter-activity', {
                detail: {
                    accounts: [...this.selectedAccounts],
                    secCodes: [...this.selectedSecCodes],
                    amountRange: this.amountRange,
                    postedDate: this.postedDate,
                    description: this.description,
                    achExceptionDecisionStatus: this.achExceptionDecisionStatus,
                },
                bubbles: true,
                composed: true,
            })
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchExceptionsActivityFilterSheet;
    }
}
